/**
 * React hook for WebSocket connection and event handling
 */

import { useEffect, useRef, useState } from 'react';
import {
  WebSocketClient,
  WebSocketEventListener,
  WebSocketMessage,
} from '../utils/websocket';

export function useWebSocket(enabled: boolean = true) {
  const [connected, setConnected] = useState(false);
  const clientRef = useRef<WebSocketClient | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Get token from localStorage
    const token = localStorage.getItem('admin_token');

    // Create WebSocket client
    const client = new WebSocketClient(undefined, token || undefined);
    clientRef.current = client;

    // Add connection status listener
    const statusListener: WebSocketEventListener = (message) => {
      if (message.type === 'pong') {
        setConnected(true);
      }
    };
    client.addListener(statusListener);

    // Connect
    client.connect();
    setConnected(true);

    // Cleanup on unmount
    return () => {
      client.removeListener(statusListener);
      client.disconnect();
      clientRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  /**
   * Subscribe to WebSocket events
   */
  const subscribe = (listener: WebSocketEventListener) => {
    if (!clientRef.current) {
      console.warn('WebSocket not initialized');
      return () => {};
    }

    clientRef.current.addListener(listener);

    // Return unsubscribe function
    return () => {
      clientRef.current?.removeListener(listener);
    };
  };

  return {
    connected,
    subscribe,
  };
}

/**
 * Hook for specific event type subscriptions
 */
export function useWebSocketEvent<T extends WebSocketMessage['type']>(
  eventType: T,
  handler: (message: Extract<WebSocketMessage, { type: T }>) => void,
  enabled: boolean = true
) {
  const { connected, subscribe } = useWebSocket(enabled);

  useEffect(() => {
    if (!enabled || !connected) {
      return;
    }

    const unsubscribe = subscribe((message) => {
      if (message.type === eventType) {
        handler(message as Extract<WebSocketMessage, { type: T }>);
      }
    });

    return unsubscribe;
  }, [eventType, handler, enabled, connected, subscribe]);

  return { connected };
}
